import { useReactiveVar } from '@apollo/client'
import { TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { projectVar, selectedVersionsVar } from '../../apollo/reactive-vars'
import { SingleSettingSection } from '../common/SingleSettingSection'

type ContainerSelectorOption = {
  name: string
  value: string | null
}

type Props = {
  handleChange: (value: string | null) => void
}

export function ContainerSelector({ handleChange }: Props): React.ReactElement {
  const { t } = useTranslation()
  const project = useReactiveVar(projectVar)
  const selectedVersions = useReactiveVar(selectedVersionsVar)

  const defaultOption = useMemo(
    () => ({
      name: t('costs.containerSelector.project'),
      value: null,
    }),
    [t],
  )
  const [selection, setSelection] =
    useState<ContainerSelectorOption>(defaultOption)

  const versionIds = selectedVersions.map((version) => version.id)
  const containerNames =
    project?.versions
      .filter((version) => versionIds.includes(version.id))
      .flatMap((version) => [
        ...version.buildings.map((b) => b.name),
        ...version.localGrids.map((l) => t(`localGridType.${l.type}`)),
      ]) ?? []

  const containerOptions = Array.from(new Set(containerNames)).map(
    (containerName) => ({
      name: containerName,
      value: containerName,
    }),
  )
  const [options, setOptions] = useState([defaultOption, ...containerOptions])

  useEffect(() => {
    if (selection.value === null) {
      setSelection(defaultOption)
    }
    setOptions([defaultOption, ...containerOptions])
    // Adding containerOptions to dependencies causes infinite render loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOption])

  const onChange = (option: ContainerSelectorOption | null) => {
    setSelection(option ?? defaultOption)
    handleChange(option ? option.value : null)
  }

  return (
    <SingleSettingSection
      title={t('costs.containerSelector.filterByContainer')}
    >
      <Autocomplete
        value={selection}
        options={options}
        getOptionLabel={(option) => option.name}
        getOptionSelected={(option, value) => option.name === value.name}
        onChange={(_, option) => onChange(option)}
        renderInput={(params) => <TextField {...params} />}
      />
    </SingleSettingSection>
  )
}
